import type { StoriesStore } from "./stories.store";

/**
 * Selectors for common store access patterns
 * These provide typed, reusable selectors for frequently accessed state slices
 */

/**
 * Selects filter-related state
 */
export const selectFilters = (store: StoriesStore) => ({
  search: store.search,
  status: store.status,
  owner: store.owner,
  folderId: store.folderId,
  types: store.types,
  locationFolderIds: store.locationFolderIds,
  dateFilter: store.dateFilter,
  isFavoritesView: store.isFavoritesView,
});

/**
 * Selects sort-related state
 */
export const selectSort = (store: StoriesStore) => ({
  sortBy: store.sortBy,
  sortOrder: store.sortOrder,
});

/**
 * Selects view type and related state
 */
export const selectView = (store: StoriesStore) => ({
  type: store.type,
  isFavoritesView: store.isFavoritesView,
  scopedStoryId: store.scopedStoryId,
});

/**
 * Selects duplication-related state
 */
export const selectDuplication = (store: StoriesStore) => ({
  duplicatingStoryId: store.duplicatingStoryId,
  duplicatedStoryId: store.duplicatedStoryId,
  pendingDuplicatesCount: store.pendingDuplicatesCount,
});

/**
 * Selects the multi-select slice
 */
export const selectSelection = (store: StoriesStore) => ({
  selected: store.selected,
  selectionAnchorId: store.selectionAnchorId,
  selectOnly: store.selectOnly,
  toggleSelected: store.toggleSelected,
  selectRange: store.selectRange,
  clearSelection: store.clearSelection,
});

/**
 * The "Created by me" view: the Stories root with the Owner filter set to the
 * signed-in user. It is not a route of its own — the sidebar entry sets the
 * filter — but the page treats it as a place: its own breadcrumb, no folders,
 * no Owner pill, the sidebar row filled. Every one of those reads this, so the
 * definition lives in one spot.
 *
 * An open folder, Favorites and a story's versions page all take precedence:
 * picking yourself in the Owner pill inside a folder is a plain filter there,
 * not this view.
 */
export const isCreatedByMeView = (
  store: Pick<
    StoriesStore,
    "owner" | "folderId" | "isFavoritesView" | "scopedStoryId"
  >,
  currentUserId: string,
): boolean =>
  !!currentUserId &&
  store.owner === currentUserId &&
  !store.folderId &&
  !store.isFavoritesView &&
  !store.scopedStoryId;

/**
 * Selects all filter and sort state for query building
 */
export const selectQueryParams = (store: StoriesStore) => ({
  search: store.search,
  status: store.status,
  owner: store.owner,
  sortBy: store.sortBy,
  sortOrder: store.sortOrder,
  folderId: store.folderId,
  type: store.type,
  types: store.types,
  locationFolderIds: store.locationFolderIds,
  dateFilter: store.dateFilter,
  isFavoritesView: store.isFavoritesView,
});
